import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Container ,
  Paper,
  Stack
} from '@mui/material';

// Menu Item Skeleton
export const MenuItemSkeleton = ({ count = 6, viewMode = 'grid' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getGridSize = () => {
    if (isMobile) return 12;
    if (isTablet) return 6;
    return 4;
  };

  return (
    <Grid container spacing={isMobile ? 1 : 2}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={getGridSize()} key={index}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              },
            }}
          >
            <Skeleton
              variant="rectangular"
              height={isMobile ? 180 : 200}
              animation="wave"
              sx={{
                bgcolor: 'grey.200',
                '&::after': {
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                },
              }}
            />
            <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Skeleton variant="text" width="70%" height={24} animation="wave" />
                <Skeleton variant="circular" width={24} height={24} animation="wave" />
  </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Skeleton variant="text" width="60%" height={16} animation="wave" />
                <Skeleton variant="text" width="20%" height={16} animation="wave" />
      </Box>
              
              <Skeleton variant="text" width="100%" height={14} animation="wave" sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={14} animation="wave" sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                <Skeleton variant="text" width="30%" height={20} animation="wave" />
                <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
    </CardContent>
  </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Category Skeleton
export const CategorySkeleton = ({ count = 6 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, px: 1 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ minWidth: 120, flexShrink: 0 }}>
          <Skeleton
            variant="rectangular"
            height={isMobile ? 80 : 100}
            width={isMobile ? 100 : 120}
            animation="wave"
            sx={{
              borderRadius: 2,
              bgcolor: 'grey.200',
              '&::after': {
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              },
            }}
          />
          <Skeleton variant="text" width="100%" height={16} animation="wave" sx={{ mt: 1 }} />
        </Box>
      ))}
    </Box>
  );
};

// Hero Section Skeleton
export const HeroSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        position: 'relative',
        height: isMobile ? 300 : 400,
        overflow: 'hidden',
        borderRadius: 3,
        mb: 4,
      }}
    >
      <Skeleton
        variant="rectangular"
        height="100%"
        width="100%"
        animation="wave"
        sx={{
          bgcolor: 'grey.200',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: 'white',
          zIndex: 2,
        }}
      >
        <Skeleton variant="text" width={300} height={40} animation="wave" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />
        <Skeleton variant="text" width={200} height={24} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
      </Box>
      </Box>
  );
};

// Outlet Card Skeleton
export const OutletCardSkeleton = ({ count = 3 }) => {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} animation="wave" />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width="70%" height={20} animation="wave" />
                <Skeleton variant="text" width="50%" height={16} animation="wave" />
        </Box>
      </Box>
            <Skeleton variant="text" width="100%" height={14} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={14} animation="wave" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={36} animation="wave" sx={{ borderRadius: 2 }} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Search Bar Skeleton
export const SearchBarSkeleton = () => {
  return (
    <Box sx={{ mb: 3 }}>
      <Skeleton
        variant="rectangular"
        height={56}
        animation="wave"
        sx={{
          borderRadius: 3,
          bgcolor: 'grey.200',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
          },
        }}
      />
    </Box>
  );
};

// Filter Section Skeleton
export const FilterSectionSkeleton = () => {
  return (
    <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
      <Skeleton variant="text" width="30%" height={24} animation="wave" sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width={80}
            height={32}
            animation="wave"
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Box>
    </Box>
  );
};

// Page Header Skeleton
export const PageHeaderSkeleton = () => {
  return (
    <Box sx={{ mb: 4, textAlign: 'center' }}>
      <Skeleton variant="text" width="60%" height={40} animation="wave" sx={{ mx: 'auto', mb: 2 }} />
      <Skeleton variant="text" width="40%" height={24} animation="wave" sx={{ mx: 'auto' }} />
    </Box>
  );
};

// Cart Item Skeleton
export const CartItemSkeleton = ({ count = 3 }) => {
  return (
    <Box>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Skeleton variant="rectangular" width={60} height={60} animation="wave" sx={{ borderRadius: 1 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={20} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="50%" height={16} animation="wave" sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton variant="text" width="30%" height={16} animation="wave" />
              <Skeleton variant="rectangular" width={80} height={24} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
              </Box>
            ))}
              </Box>
  );
};

// Loading Overlay
export const LoadingOverlay = ({ message = "Loading..." }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Skeleton
          variant="circular"
          width={60}
          height={60}
          animation="wave"
          sx={{
            mb: 2,
            bgcolor: 'primary.main',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            },
          }}
        />
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
          {message}
        </Typography>
            </Box>
    </Box>
  );
};

// Dashboard Skeleton
export const DashboardSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
    <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width="40%" height={40} animation="wave" sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" height={24} animation="wave" />
    </Box>

    <Grid container spacing={3} sx={{ mb: 4 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} animation="wave" />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="70%" height={20} animation="wave" />
                  <Skeleton variant="text" width="50%" height={16} animation="wave" />
                </Box>
              </Box>
              <Skeleton variant="text" width="100%" height={14} animation="wave" />
          </Card>
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width="30%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={300} animation="wave" />
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width="40%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={32} height={32} animation="wave" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="80%" height={16} animation="wave" />
                    <Skeleton variant="text" width="60%" height={14} animation="wave" />
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Delivery Management Skeleton
export const DeliveryManagementSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="text" width="30%" height={32} animation="wave" />
        <Skeleton variant="rectangular" width={120} height={36} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
      
      <Card sx={{ p: 3, mb: 3 }}>
        <Skeleton variant="text" width="25%" height={24} animation="wave" sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" width={100} height={32} animation="wave" sx={{ borderRadius: 2 }} />
          ))}
        </Box>
        <Skeleton variant="rectangular" height={400} animation="wave" />
      </Card>
    </Box>
  );
};

// Page Skeleton (Generic)
export const PageSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="40%" height={40} animation="wave" sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" height={200} animation="wave" sx={{ mb: 3, borderRadius: 2 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} variant="rectangular" width={120} height={40} animation="wave" sx={{ borderRadius: 2 }} />
        ))}
      </Box>
      <Skeleton variant="rectangular" height={300} animation="wave" sx={{ borderRadius: 2 }} />
    </Box>
  );
};

// Skeleton Table
export const SkeletonTable = ({ rows = 5, columns = 4 }) => {
  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'grey.50' }}>
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} variant="text" width="100%" height={20} animation="wave" />
        ))}
      </Box>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box key={rowIndex} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} variant="text" width="100%" height={16} animation="wave" />
          ))}
        </Box>
      ))}
    </Box>
  );
};




export const HomePageSkeleton = () => {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Hero Section Skeleton */}
      <Box sx={{ position: 'relative', height: { xs: '60vh', md: '70vh' }, bgcolor: 'grey.200' }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>

      {/* Categories Section Skeleton */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "#fff" }}>
        <Container maxWidth="xl">
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 4 } }}>
            <Skeleton variant="text" width="40%" height={40} sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mx: 'auto' }} />
          </Box>

          {/* Categories Scroll */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Left Arrow */}
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            
            {/* Categories */}
            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3, md: 4 }, overflow: 'hidden', flex: 1 }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Box key={index} sx={{ flex: '0 0 auto', width: { xs: 140, sm: 180, md: 200 } }}>
                  <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: '16px' }} />
                </Box>
              ))}
            </Box>

            {/* Right Arrow */}
            <Skeleton variant="circular" width={40} height={40} sx={{ ml: 2 }} />
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section Skeleton */}
      <Box sx={{ py: { xs: 6, md: 10 }, background: "linear-gradient(to bottom, #fff7f2, #fbe4d8)" }}>
        <Container>
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Skeleton variant="text" width="30%" height={40} sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant="text" width="50%" height={24} sx={{ mx: 'auto' }} />
          </Box>

          {/* Features Grid */}
          <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} justifyContent="center">
            {Array.from({ length: 4 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Skeleton variant="rectangular" height={320} sx={{ borderRadius: '20px' }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section Skeleton */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "#fff" }}>
        <Container maxWidth="xl">
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Skeleton variant="text" width="25%" height={40} sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant="text" width="45%" height={24} sx={{ mx: 'auto' }} />
          </Box>

          {/* Services Grid */}
          <Grid container spacing={{ xs: 3, sm: 4, md: 4 }} justifyContent="center">
            {Array.from({ length: 4 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ textAlign: 'center', borderRadius: '16px', p: 3 }}>
                  <Skeleton variant="circular" width={100} height={100} sx={{ mx: 'auto', mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={24} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton variant="text" width="90%" height={20} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton variant="text" width="90%" height={20} sx={{ mx: 'auto', mb: 3 }} />
                  <Skeleton variant="rounded" width="100%" height={40} />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section Skeleton */}
      <Box sx={{ py: { xs: 6, md: 10 }, background: "linear-gradient(180deg, #fffaf5 0%, #ffe8dc 100%)" }}>
        <Container maxWidth="lg">
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Skeleton variant="text" width="35%" height={40} sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mx: 'auto' }} />
          </Box>

          {/* Image + Text Section */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={420} sx={{ borderRadius: '20px' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="85%" height={20} />
            </Grid>
          </Grid>

          {/* Signature Dishes Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Skeleton variant="text" width="30%" height={40} sx={{ mx: 'auto' }} />
          </Box>

          {/* Signature Dishes Carousel */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton 
                  key={index} 
                  variant="rectangular" 
                  width={300} 
                  height={220} 
                  sx={{ borderRadius: '12px', flexShrink: 0 }} 
                />
              ))}
            </Box>
          </Box>

          {/* Final Quote Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Skeleton variant="text" width="70%" height={32} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} />
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section Skeleton */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Skeleton variant="text" width="25%" height={40} sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mx: 'auto' }} />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 3, overflow: 'hidden' }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Box key={index} sx={{ flex: '0 0 auto', width: { xs: 280, md: 350 } }}>
                <Card sx={{ p: 3, borderRadius: '12px' }}>
                  <Skeleton variant="circular" width={60} height={60} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Footer Skeleton */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Skeleton variant="text" width="60%" height={30} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="75%" height={20} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};
// Checkout Page Skeleton
export const CheckoutPageSkeleton = () => {
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Skeleton variant="text" width="30%" height={32} animation="wave" sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width="25%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width={120} height={32} animation="wave" sx={{ borderRadius: 2 }} />
            </Box>
            <Skeleton variant="rectangular" height={200} animation="wave" sx={{ borderRadius: 2 }} />
          </Card>
          
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width="30%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="rectangular" width={60} height={60} animation="wave" sx={{ borderRadius: 1 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="70%" height={20} animation="wave" />
                    <Skeleton variant="text" width="50%" height={16} animation="wave" />
                  </Box>
                  <Skeleton variant="text" width="20%" height={20} animation="wave" />
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Skeleton variant="text" width="40%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Skeleton variant="text" width="60%" height={16} animation="wave" />
                  <Skeleton variant="text" width="30%" height={16} animation="wave" />
                </Box>
              ))}
            </Box>
            <Skeleton variant="rectangular" width="100%" height={48} animation="wave" sx={{ borderRadius: 2 }} />
        </Card>
      </Grid>
    </Grid>
  </Box>
);
};

// Menu Page Skeleton
export const MenuPageSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="40%" height={40} animation="wave" sx={{ mb: 3 }} />
      
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width="20%" height={24} animation="wave" sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" width={120} height={100} animation="wave" sx={{ borderRadius: 2, flexShrink: 0 }} />
          ))}
        </Box>
      </Box>
      
      <Grid container spacing={2}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <Skeleton variant="rectangular" height={200} animation="wave" />
              <CardContent sx={{ p: 2 }}>
                <Skeleton variant="text" width="80%" height={20} animation="wave" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={14} animation="wave" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton variant="text" width="30%" height={18} animation="wave" />
                  <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
  </Box>
);
};


// Delivery Page Skeleton
export const DeliveryPageSkeleton = () => {
  return (
    <Box sx={{ bgcolor: "white", minHeight: "100vh", mt: 10 }}>
      {/* Hero Section Skeleton */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "20vh", sm: '20vh', md: "50vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.200",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", py: { xs: 3, md: 4 } }}>
            {/* Icon Skeleton */}
            <Skeleton 
              variant="circular" 
              width={70} 
              height={70} 
              sx={{ mx: 'auto', mb: 2 }}
            />
            
            {/* Title Skeleton */}
            <Skeleton 
              variant="text" 
              width="60%" 
              height={60} 
              sx={{ mx: 'auto', mb: 2 }}
            />
            
            {/* Subtitle Skeleton */}
            <Skeleton 
              variant="text" 
              width="80%" 
              height={40} 
              sx={{ mx: 'auto', mb: 3 }}
            />
            
            {/* Chips Skeleton */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
              {[1, 2, 3].map((item) => (
                <Skeleton
                  key={item}
                  variant="rounded"
                  width={160}
                  height={40}
                  sx={{ borderRadius: 20 }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Search and Filter Section Skeleton */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "1440px",
          margin: "0 auto",
          py: { xs: 3, sm: 4, md: 5 },
          px: { xs: 1, sm: 2 },
          bgcolor: "#fff",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 3, sm: 4, md: 5 },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        {/* Search Field Skeleton */}
        <Skeleton
          variant="rounded"
          sx={{
            flex: { xs: "1 1 auto", sm: "1 1 70%" },
            height: { xs: 48, sm: 48, md: 52, lg: 56 },
            borderRadius: 2,
          }}
        />

        {/* Action Buttons Skeleton */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            gap: { xs: 2, sm: 3, md: 4 },
            alignItems: "center",
            justifyContent: { xs: "space-between", sm: "flex-end" },
            width: { xs: "100%", sm: "auto" },
            flex: { xs: "0 0 auto", sm: "0 0 auto" },
          }}
        >
          {/* Filter Button Skeleton */}
          <Skeleton
            variant="rounded"
            sx={{
              height: { xs: 48, sm: 48, md: 52, lg: 56 },
              width: { xs: 100, sm: 110, md: 120, lg: 130 },
              borderRadius: 2,
            }}
          />

          {/* Toggle Buttons Skeleton */}
          <Skeleton
            variant="rounded"
            sx={{
              height: { xs: 48, sm: 48, md: 52, lg: 56 },
              width: { xs: 88, sm: 92, md: 96, lg: 100 },
              borderRadius: 2,
            }}
          />
        </Box>
      </Box>

      {/* Main Content Skeleton */}
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          backgroundColor: "#fff",
          maxWidth: "1440px",
          margin: "0 auto",
          mt: 2,
          px: { xs: 1, sm: 2, md: 3 },
          gap: { xs: 2, sm: 3, md: 4 },
          height: { xs: "auto", sm: "calc(100vh - 100px)" },
          overflow: "hidden",
          mb: 2,
        }}
      >
        {/* Category Sidebar Skeleton */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "column" },
            gap: { xs: 1.5, sm: 2 },
            width: { xs: "100%", sm: "200px", md: "230px", lg: "250px" },
            flexShrink: 0,
            padding: { xs: "8px", sm: "10px", md: "12px" },
            backgroundColor: "#fff",
            position: { sm: "sticky" },
            top: { sm: "100px" },
            alignSelf: "flex-start",
            height: { sm: "calc(100vh - 120px)" },
            overflowX: { xs: "auto", sm: "hidden" },
            overflowY: { xs: "hidden", sm: "auto" },
          }}
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
                p: { xs: 1, sm: 1.5 },
                borderRadius: "14px",
              }}
            >
              <Skeleton
                variant="circular"
                sx={{
                  width: { xs: 40, sm: 45, md: 50, lg: 55 },
                  height: { xs: 40, sm: 45, md: 50, lg: 55 },
                }}
              />
              <Skeleton
                variant="text"
                sx={{
                  flex: 1,
                  height: { xs: 20, sm: 22, md: 24 },
                  borderRadius: 1,
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Menu Items Skeleton */}
        <Box sx={{ flex: 1, pr: { xs: 0, sm: 1 } }}>
          {Array.from({ length: 3 }).map((_, categoryIndex) => (
            <Box key={categoryIndex} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
              {/* Category Title Skeleton */}
              <Skeleton
                variant="text"
                sx={{
                  height: { xs: 32, sm: 40, md: 48 },
                  width: "40%",
                  mx: 'auto',
                  mb: { xs: 2, sm: 3, md: 4 },
                  borderRadius: 1,
                }}
              />

              {/* Grid View Skeleton */}
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                {Array.from({ length: 8 }).map((_, itemIndex) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={itemIndex}
                    sx={{
                      padding: { xs: "8px", sm: "12px", md: "14px" },
                    }}
                  >
                    <Card
                      sx={{
                        width: { xs: '95vw', sm: '95vw' },
                        borderRadius: "14px",
                        boxShadow: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Image Skeleton */}
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 200,
                          borderRadius: "14px 14px 0 0",
                        }}
                      />

                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          p: { xs: 1.5, sm: 2 },
                        }}
                      >
                        <Box>
                          {/* Item Header Skeleton */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: { xs: 1, sm: 2 },
                            }}
                          >
                            <Skeleton
                              variant="text"
                              sx={{
                                flex: 1,
                                height: { xs: 20, sm: 22 },
                                mr: 1,
                              }}
                            />
                            <Skeleton
                              variant="circular"
                              sx={{
                                width: { xs: 16, sm: 18 },
                                height: { xs: 16, sm: 18 },
                              }}
                            />
                          </Box>

                          {/* Description Skeleton */}
                          <Skeleton
                            variant="text"
                            sx={{
                              height: { xs: 16, sm: 18 },
                              mb: 1,
                            }}
                          />
                          <Skeleton
                            variant="text"
                            sx={{
                              height: { xs: 16, sm: 18 },
                              width: "80%",
                              mb: 1,
                            }}
                          />

                          {/* Price Skeleton */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: 'center',
                            }}
                          >
                            <Skeleton
                              variant="text"
                              sx={{
                                height: { xs: 20, sm: 22 },
                                width: 80,
                              }}
                            />
                            <Skeleton
                              variant="rounded"
                              sx={{
                                height: { xs: 20, sm: 22 },
                                width: 60,
                                borderRadius: 1,
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Add to Cart Button Skeleton */}
                        <Skeleton
                          variant="rounded"
                          sx={{
                            height: { xs: 36, sm: 40, md: 44 },
                            mt: { xs: 1.5, sm: 2 },
                            borderRadius: 2,
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer Skeleton */}
      <Box sx={{ mt: "40px" }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    </Box>
  );
};

// Individual Component Skeletons for Modular Use
export const HeroSectionSkeleton = () => (
  <Box
    sx={{
      position: "relative",
      minHeight: { xs: "20vh", sm: '20vh', md: "50vh" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "grey.200",
      overflow: "hidden",
    }}
  >
    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ textAlign: "center", py: { xs: 3, md: 4 } }}>
        <Skeleton variant="circular" width={70} height={70} sx={{ mx: 'auto', mb: 2 }} />
        <Skeleton variant="text" width="60%" height={60} sx={{ mx: 'auto', mb: 2 }} />
        <Skeleton variant="text" width="80%" height={40} sx={{ mx: 'auto', mb: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              width={160}
              height={40}
              sx={{ borderRadius: 20 }}
            />
          ))}
        </Box>
      </Box>
    </Container>
  </Box>
);

export const SearchFilterSkeleton = () => (
  <Box
    sx={{
      width: "100%",
      maxWidth: "1440px",
      margin: "0 auto",
      py: { xs: 3, sm: 4, md: 5 },
      px: { xs: 1, sm: 2 },
      bgcolor: "#fff",
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 3, sm: 4, md: 5 },
      alignItems: { xs: "stretch", sm: "center" },
    }}
  >
    <Skeleton
      variant="rounded"
      sx={{
        flex: { xs: "1 1 auto", sm: "1 1 70%" },
        height: { xs: 48, sm: 48, md: 52, lg: 56 },
        borderRadius: 2,
      }}
    />
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "row", sm: "row" },
        gap: { xs: 2, sm: 3, md: 4 },
        alignItems: "center",
        width: { xs: "100%", sm: "auto" },
        flex: { xs: "0 0 auto", sm: "0 0 auto" },
      }}
    >
      <Skeleton
        variant="rounded"
        sx={{
          height: { xs: 48, sm: 48, md: 52, lg: 56 },
          width: { xs: 100, sm: 110, md: 120, lg: 130 },
          borderRadius: 2,
        }}
      />
      <Skeleton
        variant="rounded"
        sx={{
          height: { xs: 48, sm: 48, md: 52, lg: 56 },
          width: { xs: 88, sm: 92, md: 96, lg: 100 },
          borderRadius: 2,
        }}
      />
    </Box>
  </Box>
);

export const CategorySidebarSkeleton = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "row", sm: "column" },
      gap: { xs: 1.5, sm: 2 },
      width: { xs: "100%", sm: "200px", md: "230px", lg: "250px" },
      flexShrink: 0,
      padding: { xs: "8px", sm: "10px", md: "12px" },
      backgroundColor: "#fff",
    }}
  >
    {Array.from({ length: 8 }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          p: { xs: 1, sm: 1.5 },
          borderRadius: "14px",
        }}
      >
        <Skeleton
          variant="circular"
          sx={{
            width: { xs: 40, sm: 45, md: 50, lg: 55 },
            height: { xs: 40, sm: 45, md: 50, lg: 55 },
          }}
        />
        <Skeleton
          variant="text"
          sx={{
            flex: 1,
            height: { xs: 20, sm: 22, md: 24 },
            borderRadius: 1,
          }}
        />
      </Box>
    ))}
  </Box>
);

export const MenuGridSkeleton = () => (
  <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
    {Array.from({ length: 8 }).map((_, index) => (
      <Grid
        item
        xs={12}
        sm={6}
        md={4}
        lg={3}
        key={index}
        sx={{
          padding: { xs: "8px", sm: "12px", md: "14px" },
        }}
      >
        <Card
          sx={{
            width: { xs: '95vw', sm: '95vw' },
            borderRadius: "14px",
            boxShadow: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{
              height: 200,
              borderRadius: "14px 14px 0 0",
            }}
          />
          <CardContent
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: { xs: 1, sm: 2 },
                }}
              >
                <Skeleton
                  variant="text"
                  sx={{
                    flex: 1,
                    height: { xs: 20, sm: 22 },
                    mr: 1,
                  }}
                />
                <Skeleton
                  variant="circular"
                  sx={{
                    width: { xs: 16, sm: 18 },
                    height: { xs: 16, sm: 18 },
                  }}
                />
              </Box>
              <Skeleton variant="text" sx={{ height: { xs: 16, sm: 18 }, mb: 1 }} />
              <Skeleton variant="text" sx={{ height: { xs: 16, sm: 18 }, width: "80%", mb: 1 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: 'center',
                }}
              >
                <Skeleton variant="text" sx={{ height: { xs: 20, sm: 22 }, width: 80 }} />
                <Skeleton variant="rounded" sx={{ height: { xs: 20, sm: 22 }, width: 60, borderRadius: 1 }} />
              </Box>
            </Box>
            <Skeleton
              variant="rounded"
              sx={{
                height: { xs: 36, sm: 40, md: 44 },
                mt: { xs: 1.5, sm: 2 },
                borderRadius: 2,
              }}
            />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);


export const TableReservationsSkeleton = () => (
    <>
      {/* Hero Section Skeleton */}
      <Box
        sx={{
          mt: 10,
          position: "relative",
          minHeight: { xs: "20vh", sm: '20vh', md: "55vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
          <Box sx={{ textAlign: "center", color: "white" }}>
            {/* Table Icon Skeleton */}
            <Skeleton
              variant="circular"
              sx={{
                width: { xs: 60, md: 85 },
                height: { xs: 60, md: 85 },
                mb: 2,
                mx: "auto",
                bgcolor: "grey.300"
              }}
            />

            {/* Title Skeleton */}
            <Skeleton
              variant="text"
              sx={{
                fontSize: { xs: "2rem", md: "3.5rem" },
                width: { xs: "80%", md: "60%" },
                height: { xs: 40, md: 70 },
                mx: "auto",
                mb: 1,
                bgcolor: "grey.300"
              }}
            />

            {/* Subtitle Skeleton */}
            <Skeleton
              variant="text"
              sx={{
                fontSize: { xs: "1rem", md: "1.4rem" },
                width: { xs: "90%", md: "70%" },
                height: { xs: 24, md: 32 },
                mx: "auto",
                mb: 3,
                bgcolor: "grey.300"
              }}
            />

            {/* Feature Chips Skeleton */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: { xs: 1, md: 2 },
                flexWrap: "wrap",
              }}
            >
              {[1, 2, 3].map((item) => (
                <Skeleton
                  key={item}
                  variant="rounded"
                  sx={{
                    width: { xs: 120, md: 160 },
                    height: { xs: 36, md: 40 },
                    borderRadius: 20,
                    bgcolor: "grey.300"
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Form Section Skeleton */}
      <Box
        sx={{
          mt: 2,
          py: 4,
          px: 4,
          mb: 2,
          maxWidth: '1440px',
          margin: '0 auto'
        }}
      >
        <Container maxWidth="sm" sx={{ mb: 2 }}>
          <Paper
            elevation={8}
            sx={{
              p: 3.5,
              borderRadius: 3,
              background: "#ffffff",
            }}
          >
            {/* Header Skeleton */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Skeleton
                variant="circular"
                sx={{
                  width: 50,
                  height: 50,
                  mx: "auto",
                  mb: 2,
                  bgcolor: "grey.300"
                }}
              />
              <Skeleton
                variant="text"
                sx={{
                  fontSize: "1.5rem",
                  width: "60%",
                  height: 32,
                  mx: "auto",
                  mb: 1,
                  bgcolor: "grey.300"
                }}
              />
              <Skeleton
                variant="text"
                sx={{
                  fontSize: "0.875rem",
                  width: "80%",
                  height: 20,
                  mx: "auto",
                  bgcolor: "grey.300"
                }}
              />
            </Box>

            <Stack spacing={3}>
              {/* Name & Phone Row Skeleton */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    flex: 1,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "grey.300"
                  }}
                />
                <Skeleton
                  variant="rounded"
                  sx={{
                    flex: 1,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "grey.300"
                  }}
                />
              </Box>

              {/* Email & Guests Row Skeleton */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    flex: 1,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "grey.300"
                  }}
                />
                <Skeleton
                  variant="rounded"
                  sx={{
                    flex: 1,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "grey.300"
                  }}
                />
              </Box>

              {/* Date & Time Row Skeleton */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    flex: 1,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "grey.300"
                  }}
                />
                <Skeleton
                  variant="rounded"
                  sx={{
                    flex: 1,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "grey.300"
                  }}
                />
              </Box>

              {/* Special Requests Skeleton */}
              <Skeleton
                variant="rounded"
                sx={{
                  height: 80,
                  borderRadius: 1,
                  bgcolor: "grey.300"
                }}
              />

              {/* Outlet Selection Skeleton */}
              <Skeleton
                variant="rounded"
                sx={{
                  height: 56,
                  borderRadius: 1,
                  bgcolor: "grey.300"
                }}
              />

              {/* Submit Button Skeleton */}
              <Skeleton
                variant="rounded"
                sx={{
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "grey.300"
                }}
              />
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* Footer Skeleton */}
      <Box sx={{ bgcolor: "grey.100", py: 4 }}>
        <Container maxWidth="lg">
          <Stack spacing={2}>
            <Skeleton
              variant="text"
              sx={{
                fontSize: "1.5rem",
                width: "30%",
                height: 32,
                bgcolor: "grey.300"
              }}
            />
            <Skeleton
              variant="text"
              sx={{
                fontSize: "1rem",
                width: "80%",
                height: 20,
                bgcolor: "grey.300"
              }}
            />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              {[1, 2, 3, 4].map((item) => (
                <Skeleton
                  key={item}
                  variant="circular"
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "grey.300"
                  }}
                />
              ))}
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );


export default {
  MenuItemSkeleton,
  CategorySkeleton,
  HeroSkeleton,
  OutletCardSkeleton,
  SearchBarSkeleton,
  FilterSectionSkeleton,
  PageHeaderSkeleton,
  CartItemSkeleton,
  LoadingOverlay,
  DashboardSkeleton,
  DeliveryManagementSkeleton,
  PageSkeleton,
  SkeletonTable,
  CheckoutPageSkeleton,
  MenuPageSkeleton,
  DeliveryPageSkeleton,
  HomePageSkeleton,
  TableReservationsSkeleton,
};